import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

// ─── Tipos ──────────────────────────────────────────────
type Alerta = {
  chave: string;
  tipo: 'TAREFA' | 'INFO';
  titulo: string;
  quando: string;
  dias: number;
  cor: 'verde' | 'amarelo' | 'vermelho' | 'roxo' | 'azul';
  se?: number;
  acao?: string;
  custom?: boolean;
};
type Resposta = {
  hoje: string;
  se: number;
  total: number;
  urgentes: number;
  tarefas: Alerta[];
  informativos: Alerta[];
  resumo: { total: number; urgentes: number; tarefas: number; informativos: number };
};

// ─── Helpers de cor/rótulo ──────────────────────────────
const CORES: Record<string, { bg: string; border: string; ink: string; soft: string }> = {
  vermelho: { bg: '#fdeaea', border: '#e0242b', ink: '#b81e24', soft: '#fdeaea' },
  amarelo:  { bg: '#fdf2da', border: '#e08a00', ink: '#b86f00', soft: '#fdf2da' },
  verde:    { bg: '#e6f6ec', border: '#16a34a', ink: '#128040', soft: '#e6f6ec' },
  roxo:     { bg: '#f0eafd', border: '#7c3aed', ink: '#6326c9', soft: '#f0eafd' },
  azul:     { bg: '#e8f0fd', border: '#2f6fde', ink: '#2356bb', soft: '#e8f0fd' },
};

function prazoTexto(a: Alerta): string {
  if (a.dias < 0) return `Atrasado há ${Math.abs(a.dias)} dia(s)`;
  if (a.dias === 0) return 'HOJE';
  if (a.dias === 1) return 'Amanhã';
  return `Em ${a.dias} dias`;
}

function dataBr(iso: string): string {
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
  } catch { return iso; }
}

// ─── Componente ─────────────────────────────────────────
function AlertasInner({ onVoltar }: { onVoltar?: () => void }) {
  const { token } = (useAuth() as any) || {};
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await axios.get(`${API_URL}/api/alertas/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDados(r.data);
    } catch (e: any) {
      setErro('Não consegui carregar os alertas. Tente atualizar.');
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const [agindo, setAgindo] = useState<string | null>(null);
  const acaoAlerta = useCallback(async (chave: string, acao: 'concluir' | 'soneca') => {
    setAgindo(chave);
    try {
      await axios.post(`${API_URL}/api/alertas/${acao}/${encodeURIComponent(chave)}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await carregar();
    } catch (e) { /* silencioso */ }
    finally { setAgindo(null); }
  }, [token, carregar]);

  const tarefas = dados?.tarefas ?? [];
  const infos = dados?.informativos ?? [];
  const urgentes = dados?.urgentes ?? 0;
  const total = dados?.total ?? 0;

  return (
    <div style={{ minHeight: '100vh', background: '#eef2f7', color: '#1a2532',
      fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13.5 }}>
      {/* ─── Cabeçalho escuro ─── */}
      <div style={{ background: '#030b16', color: '#f0f4f8', position: 'relative' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg,#00b8cc,#e85d26)' }} />
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '18px 24px',
          display: 'flex', alignItems: 'center', gap: 16 }}>
          {onVoltar && (
            <button onClick={onVoltar} style={{ background: 'rgba(255,255,255,0.08)',
              color: '#f0f4f8', border: 'none', borderRadius: 8, padding: '8px 14px',
              cursor: 'pointer', fontSize: 13 }}>← Voltar</button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>🔔</span>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Alertas</h1>
              {urgentes > 0 && <span style={{ width: 9, height: 9, borderRadius: '50%',
                background: '#e0242b', boxShadow: '0 0 0 4px rgba(224,36,43,.25)' }} />}
            </div>
            <div style={{ color: '#8aa0b8', fontSize: 12.5, marginTop: 2 }}>
              Seus lembretes e prazos em um só lugar
              {dados && ` · ${dados.hoje.split('-').reverse().join('/')} · SE ${dados.se}`}
            </div>
          </div>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: 8 }}>
            <KpiTopo label="URGENTE" valor={urgentes} cor="#e0242b" destaque={urgentes > 0} />
            <KpiTopo label="TAREFAS" valor={tarefas.length} cor="#00b8cc" />
            <KpiTopo label="INFORM." valor={infos.length} cor="#7c3aed" />
            <KpiTopo label="ATIVOS" valor={total} cor="#f0f4f8" />
          </div>
        </div>
      </div>

      {/* ─── Corpo claro ─── */}
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px' }}>
        {carregando && <div style={{ padding: 40, textAlign: 'center', color: '#5a6470' }}>Carregando alertas…</div>}
        {erro && <div style={{ padding: 20, background: '#fdeaea', color: '#b81e24', borderRadius: 12 }}>{erro}</div>}

        {!carregando && !erro && dados && (
          <>
            {/* Hero resumo */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '22px 24px',
              boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 20,
              borderTop: '3px solid', borderImage: 'linear-gradient(90deg,#00b8cc,#e85d26) 1' }}>
              <div style={{ fontSize: 11, letterSpacing: 1, color: '#8a929c', fontWeight: 700, textTransform: 'uppercase' }}>
                Resumo de hoje
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>
                {total === 0 ? 'Tudo em dia! 🎉' : <>Você tem <span style={{ color: '#e0242b' }}>{total} alerta{total > 1 ? 's' : ''}</span>{urgentes > 0 && <> — {urgentes} urgente{urgentes > 1 ? 's' : ''}</>}</>}
              </div>
            </div>

            {/* TAREFAS */}
            {tarefas.length > 0 && (
              <Secao titulo="Tarefas" sub="o que você precisa fazer" accent="#e85d26" count={tarefas.length}>
                {tarefas.map((a) => <CardTarefa key={a.chave} a={a} onAcao={acaoAlerta} agindo={agindo === a.chave} />)}
              </Secao>
            )}

            {/* INFORMATIVOS */}
            {infos.length > 0 && (
              <Secao titulo="Informativos" sub="para saber e planejar" accent="#7c3aed" count={infos.length}>
                {infos.map((a) => <CardInfo key={a.chave} a={a} onCiente={() => acaoAlerta(a.chave, 'soneca')} agindo={agindo === a.chave} />)}
              </Secao>
            )}

            {total === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: '#5a6470', background: '#fff',
                borderRadius: 16 }}>Nenhum alerta ativo no momento. Tudo sob controle. ✅</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Subcomponentes ─────────────────────────────────────
function KpiTopo({ label, valor, cor, destaque }: { label: string; valor: number; cor: string; destaque?: boolean }) {
  return (
    <div style={{ background: destaque ? 'rgba(224,36,43,.15)' : 'rgba(255,255,255,0.05)',
      border: `1px solid ${destaque ? 'rgba(224,36,43,.4)' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 10, padding: '8px 14px', textAlign: 'center', minWidth: 64 }}>
      <div style={{ fontSize: 9.5, letterSpacing: 1, color: '#8aa0b8', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: destaque ? '#ff6b6b' : cor }}>{valor}</div>
    </div>
  );
}

function Secao({ titulo, sub, accent, count, children }: any) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12,
        paddingLeft: 4, borderLeft: `3px solid ${accent}`, paddingTop: 2, paddingBottom: 2 }}>
        <span style={{ paddingLeft: 8, fontSize: 16, fontWeight: 800 }}>{titulo}</span>
        <span style={{ fontSize: 12, color: '#8a929c' }}>{sub}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8a929c' }}>{count} ativo{count > 1 ? 's' : ''}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function CardTarefa({ a, onAcao, agindo }: { a: Alerta; onAcao: (chave: string, acao: 'concluir' | 'soneca') => void; agindo: boolean }) {
  const c = CORES[a.cor] || CORES.verde;
  const pulsa = a.cor === 'vermelho';
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px',
      borderLeft: `5px solid ${c.border}`, boxShadow: pulsa ? `0 0 0 2px ${c.border}33` : '0 1px 3px rgba(0,0,0,.06)',
      display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: c.soft,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
        {a.titulo.includes('FAD') ? '📅' : a.titulo.includes('Ponto') ? '📍' : a.titulo.includes('Relatório') ? '📄' : '🔔'}
      </div>
      <div style={{ flex: 1 }}>
        <span style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: .5,
          color: c.ink, background: c.bg, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
          {prazoTexto(a)}{a.dias > 0 && ` · ${dataBr(a.quando)}`}
        </span>
        <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4 }}>{a.titulo}</div>
      </div>
      {/* Botões: visíveis mas inativos na Fase 2 (ações entram na Fase 4) */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => onAcao(a.chave, 'concluir')} disabled={agindo} style={{ background: '#16a34a', color: '#fff',
          border: 'none', borderRadius: 8, padding: '9px 14px', fontWeight: 700, fontSize: 13, cursor: agindo ? 'wait' : 'pointer' }}>✓ Concluído</button>
        <button onClick={() => onAcao(a.chave, 'soneca')} disabled={agindo} style={{ background: '#fff', color: '#5a6470',
          border: '1px solid #e2e7ee', borderRadius: 8, padding: '9px 14px', fontWeight: 600, fontSize: 13, cursor: agindo ? 'wait' : 'pointer' }}>💤 Soneca</button>
      </div>
    </div>
  );
}

function CardInfo({ a, onCiente, agindo }: { a: Alerta; onCiente: () => void; agindo: boolean }) {
  const c = CORES[a.cor] || CORES.roxo;
  const proximo = a.dias <= 2;
  return (
    <div style={{ background: proximo ? c.soft : '#fff', borderRadius: 14, padding: '14px 18px',
      borderLeft: `5px solid ${c.border}`, boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      display: 'flex', alignItems: 'center', gap: 16, opacity: proximo ? 1 : .92 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: c.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
        {a.titulo.includes('LIRAa') ? '🔬' : '📅'}
      </div>
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: .5, color: c.ink, textTransform: 'uppercase' }}>
          {a.dias === 0 ? 'EM ANDAMENTO / HOJE' : prazoTexto(a)}
        </span>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{a.titulo}</div>
        <div style={{ fontSize: 12, color: '#5a6470' }}>{a.se ? `SE ${a.se} · ` : ''}{dataBr(a.quando)}</div>
      </div>
      <button onClick={onCiente} disabled={agindo} style={{ background: 'transparent', color: '#8a929c',
        border: '1px solid #e2e7ee', borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 12,
        cursor: agindo ? 'wait' : 'pointer' }}>ok, ciente</button>
    </div>
  );
}

// ─── ErrorBoundary (rede de segurança) ──────────────────
class AlertasErrorBoundary extends React.Component<{ children: React.ReactNode }, { erro: boolean }> {
  constructor(props: any) { super(props); this.state = { erro: false }; }
  static getDerivedStateFromError() { return { erro: true }; }
  componentDidCatch(e: any) { console.error('Alertas erro:', e); }
  render() {
    if (this.state.erro) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#555' }}>
      Não consegui montar a tela de Alertas. Atualize a página.</div>;
    return this.props.children;
  }
}

export default function Alertas({ onVoltar }: { onVoltar?: () => void }) {
  return <AlertasErrorBoundary><AlertasInner onVoltar={onVoltar} /></AlertasErrorBoundary>;
}
